import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, Autocomplete, Box, Typography, Snackbar, Alert
} from '@mui/material';
import * as API from '../../api';
import CurrencyInput from '../CurrencyInput'; // Đường dẫn có thể cần điều chỉnh tùy cấu trúc thư mục của bạn

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

const HotelRoomDialog = ({ open, onClose, initialData, onSuccess }) => {
    const [form, setForm] = useState({
        room_code: '',
        room_name: '',
        supplier: null,
        bed_type: '',
        
        cost_price: 0,
        cost_original: 0,
        cost_currency: 'VND',
        cost_rate: 1,

        selling_price: 0,
        selling_original: 0,
        selling_currency: 'VND',
        selling_rate: 1
    });

    const [suppliers, setSuppliers] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });
    const handleCloseToast = () => setToast({ ...toast, open: false });

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                // Gọi API lọc NCC theo loại ACCOMMODATION
                const supRes = await API.fetchProvidersByCategory('ACCOMMODATION');
                
                // FIX LỖI: Trích xuất đúng mảng dữ liệu
                const dataList = supRes.data?.results ? supRes.data.results : supRes.data;
                setSuppliers(Array.isArray(dataList) ? dataList : []);
                
            } catch (err) {
                console.error("Lỗi tải danh mục Khách sạn:", err);
            }
        };
        fetchSuppliers();
    }, []);

    useEffect(() => {
        if (initialData) {
            setForm({
                ...initialData,
                supplier: initialData.supplier || null,
                
                cost_original: initialData.cost_price || 0,
                cost_currency: 'VND',
                cost_rate: 1,
                
                selling_original: initialData.selling_price || 0,
                selling_currency: 'VND',
                selling_rate: 1
            });
        } else {
            setForm({
                room_code: '', room_name: '', supplier: null, bed_type: '',
                cost_price: 0, cost_original: 0, cost_currency: 'VND', cost_rate: 1,
                selling_price: 0, selling_original: 0, selling_currency: 'VND', selling_rate: 1
            });
        }
    }, [initialData, open]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleMoneyChange = (fieldPrefix, data) => {
        setForm(prev => ({
            ...prev,
            [`${fieldPrefix}_price`]: data.vnd,
            [`${fieldPrefix}_original`]: data.original,
            [`${fieldPrefix}_currency`]: data.currency,
            [`${fieldPrefix}_rate`]: data.rate
        }));
    };

    const handleSubmit = async () => {
        if (!form.room_name || !form.supplier) {
            showToast("Vui lòng nhập Tên hạng phòng và chọn Khách sạn!", "error");
            return;
        }

        const payload = {
            ...form,
            cost_price: form.cost_price, 
            selling_price: form.selling_price
        };

        if (!payload.room_code) delete payload.room_code;

        delete payload.cost_original; delete payload.cost_currency; delete payload.cost_rate;
        delete payload.selling_original; delete payload.selling_currency; delete payload.selling_rate;

        if (payload.supplier && typeof payload.supplier === 'object') payload.supplier = payload.supplier.id;

        try {
            if (initialData?.room_code) {
                await API.updateHotelRoom(initialData.room_code, payload);
            } else {
                await API.createHotelRoom(payload);
            }
            
            showToast("Lưu hạng phòng thành công!", "success");
            setTimeout(() => onSuccess(), 500);
        } catch (e) {
            const errMsg = e.response?.data?.detail || e.response?.data?.room_code?.[0] || e.message;
            showToast('Lỗi lưu hạng phòng: ' + errMsg, "error");
        }
    };

    const rowStyle = { display: 'flex', gap: 2, width: '100%', marginBottom: 2 };
    const itemStyle = { flex: 1 };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#ed6c02', color: 'white', fontWeight: 'bold' }}>
                    {initialData ? 'CẬP NHẬT HẠNG PHÒNG' : 'THÊM MỚI HẠNG PHÒNG'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ pt: 1 }}>
                        {/* DÒNG 1 */}
                        <Box sx={rowStyle}>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Mã Phòng (Tự động)" />
                                <TextField name="room_code" size="small" fullWidth 
                                    placeholder="Hệ thống tự động sinh" value={form.room_code} 
                                    disabled />
                            </Box>
                            <Box sx={{ flex: 2 }}>
                                <CustomLabel title="Tên Hạng phòng" required />
                                <TextField name="room_name" size="small" fullWidth required 
                                    placeholder="VD: Deluxe Ocean View" 
                                    value={form.room_name} onChange={handleChange} />
                            </Box>
                        </Box>

                        {/* DÒNG 2 */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Khách sạn (NCC)" required />
                                <Autocomplete size="small" options={suppliers} 
                                    getOptionLabel={(opt) => opt.name || ''}
                                    value={suppliers.find(s => s.id === form.supplier) || null}
                                    onChange={(e, val) => setForm({...form, supplier: val ? val.id : null})}
                                    renderInput={(params) => <TextField {...params} placeholder="Chọn khách sạn..." />} />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Loại giường (Bed Type)" />
                                <TextField name="bed_type" size="small" fullWidth 
                                    placeholder="VD: Twin, Double, King..." value={form.bed_type} 
                                    onChange={handleChange} />
                            </Box>
                        </Box>

                        {/* DÒNG 3: GIÁ TIỀN */}
                        <Box sx={{ ...rowStyle, marginBottom: 0 }}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Giá Net (Cost)" />
                                <CurrencyInput 
                                    value={{
                                        original: form.cost_original,
                                        currency: form.cost_currency,
                                        rate: form.cost_rate,
                                        vnd: form.cost_price
                                    }}
                                    onChange={(data) => handleMoneyChange('cost', data)}
                                />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Giá Bán lẻ (Selling)" />
                                <CurrencyInput 
                                    value={{
                                        original: form.selling_original,
                                        currency: form.selling_currency,
                                        rate: form.selling_rate,
                                        vnd: form.selling_price
                                    }}
                                    onChange={(data) => handleMoneyChange('selling', data)}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#ed6c02' }}>Lưu dữ liệu</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={toast.open} autoHideDuration={2000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default HotelRoomDialog;